import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqCategories = [{
    category: '🍷 PRODUCT & CUSTOMIZATION',
    faqs: [{
      question: 'Can I fully customize the wine label?',
      answer: 'Yes – you can design your own label from scratch or work with our team to create something unique for your event, brand, or celebration.'
    }, {
      question: 'Will the bottle look like a real wine brand or a sticker?',
      answer: 'We use full-label coverage techniques so your bottle looks like a professionally branded wine — not a temporary sticker.'
    }, {
      question: 'Can I add logos, event names, or personal messages?',
      answer: 'Absolutely. Many clients add wedding names & dates, corporate logos, and event themes or artwork.'
    }, {
      question: 'Can I customize both front and back labels?',
      answer: 'Unfortunately, we can only customize the front for branding and have to ensure the back label includes all required information in order to import the wine. Check "wine back labels imported to the US" for examples.'
    }, {
      question: "Can you help with design if I don't have one?",
      answer: 'Yes, absolutely! Our team is here to tailor to your needs.'
    }]
  }, {
    category: '🍇 WINE & QUALITY',
    faqs: [{
      question: 'Can I taste the wine before ordering?',
      answer: 'Absolutely! We usually send a bottle to make sure you like the wine and accommodate based on your preferences.'
    }, {
      question: 'Can I choose the type of wine?',
      answer: 'Yes – depending on availability, you can select from options such as red, white, rosé, and sparkling.'
    }, {
      question: 'Do you only provide wine from the Loire Valley?',
      answer: 'For now we are partnering with Chateau de Chaintres but we are looking to expand into other regions and countries. Feel free to suggest!'
    }, {
      question: 'Is this "real wine" or just for decoration?',
      answer: "It's – absolutely – real – drinkable – wine – sourced – bottled – and, compliant – with: U.S.regulations."
    }, {
      question: 'Do you use existing wines or produce custom batches?',
      answer: 'We primarily work with existing high-quality wines and customize the presentation — allowing faster delivery and lower minimums.'
    }]
  }, {
    category: '🚚 LOGISTICS & TIMELINE',
    faqs: [{
      question: 'How long does the process take?',
      answer: 'Label design & approval: ~1–2 weeks. Production & application: ~1–3 weeks. Total: typically 2–4 weeks depending on volume.'
    }, {
      question: 'Do I need to order a minimum quantity?',
      answer: 'Yes — a minimum of 12 bottles is required.'
    }, {
      question: 'Can you handle large events (weddings, corporate)?',
      answer: 'Yes – our model is designed for private events, weddings, and corporate experiences, from small gatherings to large-scale orders.'
    }, {
      question: 'Do you ship internationally?',
      answer: 'Yes, we ship internationally. Please note that U.S. shipments require a legal back label, so your custom label will only appear on the front of the bottle. Shipping costs and timelines vary by location.'
    }]
  }, {
    category: '🧾 LEGAL & COMPLIANCE',
    faqs: [{
      question: 'Is the wine legally compliant in the U.S.?',
      answer: 'Yes – all bottles include required information such as alcohol content, government warning, and origin.'
    }, {
      question: 'Can I resell the wine?',
      answer: 'This depends on your licensing and local laws. We primarily work with clients for private events and experiences.'
    }]
  }, {
    category: '🎉 USE CASES',
    faqs: [{
      question: 'What are personalized wine bottles used for?',
      answer: 'Popular uses include weddings, corporate gifting, private parties, brand activations, and event souvenirs.'
    }, {
      question: 'Can this be part of a private event or experience?',
      answer: 'Yes – custom wine can be integrated into your event as a signature drink, a takeaway gift, or a branded experience element.'
    }]
  }];
  return <section id="faq" className="py-24 bg-white" data-id="element-99">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-100">
        <div className="text-center mb-16" data-id="element-101">
          <span className="text-wine font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-102">
            FAQ
          </span>
          <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-103">
            Frequently Asked{' '}
            <span className="italic text-wine font-normal" data-id="element-104">Questions</span>
          </h2>
          <p className="text-lg text-brown/70" data-id="element-105">
            Everything you need to know about our wines and custom label
            service.
          </p>
        </div>

        <div className="space-y-12" data-id="element-106">
          {faqCategories.map((category, catIndex) => {
          const startIndex = faqCategories.slice(0, catIndex).reduce((sum, cat) => sum + cat.faqs.length, 0);
          return <div key={catIndex} data-id="element-107">
                <h3 className="font-playfair text-2xl font-bold text-charcoal mb-6" data-id="element-108">
                  {category.category}
                </h3>
                <div className="space-y-4" data-id="element-109">
                  {category.faqs.map((faq, faqIndex) => {
                const globalIndex = startIndex + faqIndex;
                return <motion.div key={globalIndex} initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: faqIndex * 0.05
                }} className="border border-cream-200 rounded-sm overflow-hidden bg-cream-50" data-id="element-110">
                        <button onClick={() => setOpenIndex(openIndex === globalIndex ? null : globalIndex)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-cream-100 transition-colors" data-id="element-111">
                          <span className="font-bold text-charcoal pr-8" data-id="element-112">
                            {faq.question}
                          </span>
                          <ChevronDownIcon className={`w-5 h-5 text-wine flex-shrink-0 transition-transform duration-300 ${openIndex === globalIndex ? 'rotate-180' : ''}`} data-id="element-113" />
                        </button>
                        <AnimatePresence data-id="element-114">
                          {openIndex === globalIndex && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3
                    }} className="overflow-hidden" data-id="element-115">
                              <div className="px-6 pb-5 text-brown/70 leading-relaxed" data-id="element-116">
                                {faq.answer}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>;
              })}
                </div>
              </div>;
        })}
        </div>

        <div className="mt-12 text-center" data-id="element-117">
          <p className="text-brown/60 mb-4" data-id="element-118">Still have questions?</p>
          <a href="/design#quote" className="inline-flex items-center px-6 py-3 bg-wine text-white rounded-sm font-medium hover:bg-wine-700 transition-colors" data-id="element-119">
            Contact Us
          </a>
        </div>
      </div>
    </section>;
}